import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button as AntdButton,
  Checkbox,
  Form,
  Input as AntdInput,
  Modal,
  Space,
  Typography,
} from "antd";
import {
  BookOpen,
  Check,
  Eye,
  FileText,
  Globe,
  Link as LinkIcon,
  Pencil,
  Plus,
  Trash2,
  Upload as UploadIcon,
  X,
} from "lucide-react";
import VirtualList from "rc-virtual-list";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  createScrapWebsite,
  createTextData,
  deleteFile,
  getFile,
  getFileContent,
  getFiles,
  updateFile,
} from "../../../../services/assistant";
import { updatedSince } from "../../../../utils";
import { Button } from "../../../../components/ui/button";
import { Card } from "../../../../components/ui/card";
import { Avatar } from "../../../../components/ui/avatar";
import { Badge } from "../../../../components/ui/badge";
import { ConfirmDialog } from "../../../../components/ui/dialog";
import { EmptyState } from "../../../../components/ui/empty-state";
import { Pagination } from "../../../../components/ui/pagination";
import { Tooltip } from "../../../../components/ui/tooltip";
import { Spinner } from "../../../../components/ui/spinner";
import { cn } from "../../../../lib/cn";
import { toast } from 'sonner'

const defaultUrl = import.meta.env.VITE_SERVICE;

const progressObj = {
  READY_TO_PROCESS: 0,
  CONVERTING_TO_PDF: 25,
  GENERATING_VECTORS: 50,
  SAVING_VECTORS: 75,
  DONE: 100,
};

const progressDoc = {
  READY_TO_PROCESS: "Aguardando",
  CONVERTING_TO_PDF: "Convertendo",
  GENERATING_VECTORS: "Treinando",
  SAVING_VECTORS: "Salvando",
  ERROR: "Erro",
  DONE: "Pronto",
  UPLOADING: "Enviando",
};

const progressSite = {
  ...progressDoc,
  CONVERTING_TO_PDF: "Coletando",
};

export default function Knowledge({ id }) {
  const [siteOpen, setSiteOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [fileList, setFileList] = useState([]);

  return (
    <>
      <Site siteOpen={siteOpen} setFileList={setFileList} setSiteOpen={setSiteOpen} id={id} />
      <Text textOpen={textOpen} setFileList={setFileList} setTextOpen={setTextOpen} id={id} />

      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <BookOpen size={18} style={{ color: "var(--dash-primary)" }} />
            <h2
              className="text-xl font-semibold tracking-tight"
              style={{ color: "var(--dash-text)" }}
            >
              Base de conhecimento
            </h2>
          </div>
          <p className="text-xs" style={{ color: "var(--dash-text-muted)" }}>
            Treine seu assistente com documentos, textos e sites.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <KnowledgeTile
            icon={Globe}
            title="Adicionar site"
            description="Treine com URLs e crawl recursivo de páginas relacionadas."
            onClick={() => setSiteOpen(true)}
          />
          <KnowledgeTile
            icon={FileText}
            title="Adicionar texto"
            description="Cole conhecimento direto via texto livre."
            onClick={() => setTextOpen(true)}
          />
        </div>

        <Documents fileList={fileList} setFileList={setFileList} id={id} />
      </div>
    </>
  );
}

// eslint-disable-next-line no-unused-vars
function KnowledgeTile({ icon: TileIcon, title, description, onClick }) {
  return (
    <Card
      interactive
      onClick={onClick}
      className="group flex items-center gap-4 p-5"
    >
      <div
        className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
        }}
      >
        <div
          className="absolute -inset-2 -z-10 rounded-2xl opacity-0 blur-xl transition-opacity group-hover:opacity-50"
          style={{ background: "var(--dash-primary)" }}
        />
        <TileIcon size={20} strokeWidth={1.8} style={{ color: "var(--dash-primary)" }} />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h3
          className="text-sm font-semibold"
          style={{ color: "var(--dash-text)" }}
        >
          {title}
        </h3>
        <p
          className="text-xs leading-relaxed"
          style={{ color: "var(--dash-text-muted)" }}
        >
          {description}
        </p>
      </div>
      <Plus
        size={18}
        className="ml-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        style={{ color: "var(--dash-primary)" }}
      />
    </Card>
  );
}

function Documents({ id, fileList, setFileList }) {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);

  const update = useCallback(() => {
    setLoading(true);
    getFiles({ assistantID: id, page, perPage })
      .then(({ data, total }) => {
        setTotal(total);
        setFileList(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, page, perPage, setFileList]);

  useEffect(update, [id, page, perPage, update]);

  const uploadFile = useCallback(
    (file) => {
      const tempId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setFileList((old) => [
        {
          id: tempId,
          title: file.name,
          status: "UPLOADING",
          percent: 0,
          assistantID: id,
        },
        ...old,
      ]);

      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append("doc", file);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          setFileList((old) =>
            old.map((item) => (item.id === tempId ? { ...item, percent } : item))
          );
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            setFileList((old) =>
              old.map((item) =>
                item.id === tempId ? { ...response.data, ...response } : item
              )
            );
          } catch (err) {
            console.error(err);
            toast.error("Falha ao processar resposta do upload");
          }
        } else {
          toast.error("Falha no upload");
          setFileList((old) => old.filter((item) => item.id !== tempId));
        }
      });

      xhr.addEventListener("error", () => {
        toast.error("Erro de rede no upload");
        setFileList((old) => old.filter((item) => item.id !== tempId));
      });

      xhr.open("POST", `${defaultUrl}/assistant/${id}/file/`);
      xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("token")}`);
      xhr.send(formData);
    },
    [id, setFileList]
  );

  return (
    <div className="flex flex-col gap-4">
      <DropZone
        onFiles={(files) => Array.from(files).forEach(uploadFile)}
        disabled={loading}
      />
      {loading ? (
        <Card className="flex items-center justify-center py-12">
          <Spinner size={20} />
        </Card>
      ) : fileList.length === 0 ? (
        <Card>
          <EmptyState
            icon={UploadIcon}
            title="Nenhum arquivo ainda"
            description="Arraste arquivos acima ou adicione sites e textos para treinar seu assistente."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {fileList.map((item) => (
            <Document
              key={item?.id}
              onDelete={() =>
                setFileList((old) => old.filter((file) => file.id !== item.id))
              }
              {...item}
            />
          ))}
          <Pagination
            page={page}
            pageSize={perPage}
            total={total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

function DropZone({ onFiles, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onFiles(e.target.files);
      e.target.value = "";
    }
  };

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={cn(
        "relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 transition-all duration-200",
        disabled && "cursor-not-allowed opacity-50"
      )}
      style={{
        borderColor: dragActive
          ? "var(--dash-primary)"
          : "var(--dash-border-strong)",
        background: dragActive
          ? "var(--dash-primary-soft)"
          : "var(--dash-surface-subtle)",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".PDF,.MP3,.MP4,.MPEG,.TXT,.MD"
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl"
        style={{
          background: dragActive
            ? "var(--dash-primary)"
            : "var(--dash-primary-soft)",
          color: dragActive ? "#fff" : "var(--dash-primary)",
          border: "1px solid var(--dash-primary-border)",
          transition: "all 0.2s",
        }}
      >
        <UploadIcon size={20} strokeWidth={1.8} />
      </div>
      <div className="flex flex-col items-center gap-1">
        <p
          className="text-sm font-semibold"
          style={{ color: "var(--dash-text)" }}
        >
          {dragActive ? "Solte os arquivos" : "Clique ou arraste arquivos"}
        </p>
        <p
          className="text-[11px]"
          style={{ color: "var(--dash-text-muted)" }}
        >
          PDF · MP3 · TXT · MP4 · Markdown · MPEG
        </p>
      </div>
    </div>
  );
}

const STATUS_BADGE_VARIANT = {
  DONE: "success",
  ERROR: "danger",
  UPLOADING: "info",
};

function Document({
  title: initialTitle,
  status: initialStatus,
  link: initialLink,
  onDelete,
  type,
  assistantID,
  createdAt,
  id,
  target,
  percent: initialPercent,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(initialStatus);
  const [title, setTitle] = useState(initialTitle);
  const [link, setLink] = useState(initialLink);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form] = Form.useForm();
  const [percent, setPercent] = useState(initialPercent || 0);

  useEffect(() => {
    form.setFieldsValue({ link, title });
  }, [link, title, form]);

  useEffect(() => {
    setTitle(initialTitle);
    setLink(initialLink);
    if (typeof initialPercent === "number") setPercent(initialPercent);
    if (!target) return;
    target.addEventListener?.("progress", (e) => {
      setPercent((e.loaded / e.total) * 100);
    });
  }, [target, initialTitle, initialLink, initialPercent]);

  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const finishSave = (values) => {
    setSaving(true);
    updateFile({ assistantID, fileId: id, data: values })
      .then((res) => {
        setLink(res.data.link);
        setTitle(res.data.title);
      })
      .catch((e) => {
        toast.error("Falha ao atualizar documento");
        console.error(e);
      })
      .finally(() => {
        setIsEditing(false);
        setSaving(false);
      });
  };

  const updateStatus = useCallback(() => {
    getFile({ assistantID, fileId: id })
      .then(({ data: { status: s } }) => setStatus(s))
      .catch(console.error);
  }, [assistantID, id]);

  useEffect(() => {
    let interval;
    if (status !== "DONE" && status !== "ERROR" && status !== "UPLOADING") {
      updateStatus();
      interval = setInterval(updateStatus, 5000);
    }
    return () => clearInterval(interval);
  }, [status, updateStatus]);

  const delDoc = () => {
    setLoading(true);
    deleteFile({ assistantID, fileId: id })
      .then(() => {
        onDelete();
        toast.success("Arquivo deletado com sucesso");
        setConfirmOpen(false);
      })
      .catch((e) => {
        toast.error("Falha ao deletar documento");
        console.error(e);
      })
      .finally(() => setLoading(false));
  };

  const isProcessing = status !== "DONE" && status !== "ERROR" && status !== "UPLOADING";
  const statusLabel =
    type?.type === "URL" ? progressSite[status] : progressDoc[status];

  return (
    <>
      <Card className="flex items-center gap-4 p-4 transition-all hover:border-[var(--dash-primary-border)]">
        <DocumentAvatar type={type} status={status} percent={percent} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3
            className="truncate text-sm font-semibold"
            style={{ color: "var(--dash-text)" }}
          >
            {title}
          </h3>
          <div className="flex items-center gap-2">
            {status !== "DONE" ? (
              <Badge variant={STATUS_BADGE_VARIANT[status] || "warning"}>
                {statusLabel}
              </Badge>
            ) : (
              <span
                className="text-[11px]"
                style={{ color: "var(--dash-text-muted)" }}
              >
                Criado {updatedSince(new Date(createdAt))} atrás
              </span>
            )}
            {link && status === "DONE" && (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-[11px] font-medium transition-colors"
                style={{ color: "var(--dash-primary)" }}
              >
                <LinkIcon size={11} />
                <span className="max-w-[200px] truncate">{link}</span>
              </a>
            )}
          </div>
          {status === "UPLOADING" && (
            <div
              className="mt-1 h-1 w-full overflow-hidden rounded-full"
              style={{ background: "var(--dash-surface-hover)" }}
            >
              <div
                className="h-full transition-[width] duration-200"
                style={{
                  width: `${percent}%`,
                  background: "linear-gradient(90deg, #ad34fe 0%, #be5bff 100%)",
                }}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Tooltip content="Visualizar">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={loading || status !== "DONE"}
              onClick={() => setIsViewing(true)}
            >
              <Eye size={14} />
            </Button>
          </Tooltip>
          <Tooltip content="Editar">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={loading || status !== "DONE"}
              onClick={() => setIsEditing(true)}
            >
              <Pencil size={13} />
            </Button>
          </Tooltip>
          <Tooltip content="Deletar">
            <Button
              variant="danger"
              size="icon-sm"
              disabled={loading || isProcessing}
              loading={loading}
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 size={13} />
            </Button>
          </Tooltip>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={delDoc}
        title="Deletar documento?"
        description="Esta ação é irreversível e removerá o conteúdo da base de conhecimento do assistente."
        confirmLabel="Deletar"
        loading={loading}
      />

      <Modal
        open={isEditing}
        okText={"Salvar"}
        onCancel={() => setIsEditing(false)}
        title={"Editar documento"}
        onOk={form.submit}
        confirmLoading={saving}
        cancelText={"Cancelar"}
      >
        <DocumentEdit form={form} onFinish={finishSave} />
      </Modal>

      <Modal
        open={isViewing}
        onOk={() => setIsViewing(false)}
        onCancel={() => setIsViewing(false)}
        title={"Visualizando documento"}
        width={"80vw"}
        cancelText={"Fechar"}
      >
        <DocumentView assistantID={assistantID} fileId={id} />
      </Modal>
    </>
  );
}

function DocumentAvatar({ type, status, percent }) {
  if (status === "ERROR") {
    return <Avatar variant="danger" icon={X} size="lg" />;
  }
  if (status === "UPLOADING") {
    return (
      <div
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
        }}
      >
        <span
          className="text-[10px] font-bold tabular-nums"
          style={{ color: "var(--dash-primary)" }}
        >
          {Math.round(percent || 0)}%
        </span>
      </div>
    );
  }
  if (status !== "DONE") {
    return (
      <div
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
        }}
      >
        <Spinner size={16} style={{ color: "var(--dash-primary)" }} />
        <div
          className="absolute -bottom-1 left-0 right-0 h-0.5 overflow-hidden rounded-full"
          style={{ background: "var(--dash-surface-hover)" }}
        >
          <div
            className="h-full transition-[width] duration-300"
            style={{
              width: `${progressObj[status] || 0}%`,
              background: "linear-gradient(90deg, #ad34fe 0%, #be5bff 100%)",
            }}
          />
        </div>
      </div>
    );
  }
  const Icon = type?.type === "URL" ? LinkIcon : FileText;
  return <Avatar variant="primary" icon={Icon} size="lg" />;
}

function Site({ id, setSiteOpen, siteOpen, setFileList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = useCallback(
    async (values) => {
      await form.validateFields();
      setLoading(true);
      createScrapWebsite({ assistantID: id, data: values })
        .then(({ data }) => {
          setSiteOpen(false);
          setFileList((old) => [data, ...old]);
        })
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao requisitar treino.");
        })
        .finally(() => setLoading(false));
    },
    [form, id, setFileList, setSiteOpen]
  );

  return (
    <Modal
      open={siteOpen}
      okText={"Treinar"}
      confirmLoading={loading}
      cancelText={"Cancelar"}
      onOk={form.submit}
      title={"Informações do site"}
      onCancel={() => {
        setSiteOpen(false);
        form.resetFields();
      }}
    >
      <Space direction={"vertical"} style={{ width: "100%" }}>
        <Typography.Text>
          O assistente será treinado com todas as informações contidas no site e nos
          links relacionados.
        </Typography.Text>
        <Form layout={"vertical"} form={form} onFinish={onFinish}>
          <Form.Item
            label={"Titulo"}
            name={"title"}
            rules={[
              { required: true, message: "O titulo é obrigatório" },
              { min: 3, max: 48, message: "O titulo deve ter entre 3 e 48 caracteres" },
            ]}
          >
            <AntdInput placeholder={"Site do google"} />
          </Form.Item>
          <Form.Item
            label={"URL"}
            name={"url"}
            rules={[
              { required: true, message: "O URL do site é obrigatório" },
              {
                pattern:
                  // eslint-disable-next-line no-useless-escape
                  /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,10}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/,
                message: "URL inválido",
              },
            ]}
          >
            <AntdInput placeholder={"https://google.com"} />
          </Form.Item>
          <Form.Item
            label={"Recursivo"}
            help={
              "Ao ativar essa opção todos os links relacionados ao domínio serão buscados dentro da página informada."
            }
            name={"recursive"}
            valuePropName="checked"
          >
            <Checkbox>Ativar busca recursiva</Checkbox>
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <AntdButton htmlType={"submit"} />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
}

function Text({ id, setTextOpen, textOpen, setFileList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = useCallback(
    async (values) => {
      await form.validateFields();
      setLoading(true);
      createTextData({ assistantID: id, data: values })
        .then(({ data }) => {
          setTextOpen(false);
          setFileList((old) => [data, ...old]);
        })
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao requisitar treino.");
        })
        .finally(() => setLoading(false));
    },
    [form, id, setFileList, setTextOpen]
  );

  return (
    <Modal
      open={textOpen}
      okText={"Treinar"}
      confirmLoading={loading}
      cancelText={"Cancelar"}
      onOk={form.submit}
      title={"Informações de treinamento"}
      onCancel={() => {
        setTextOpen(false);
        form.resetFields();
      }}
    >
      <Space direction={"vertical"} style={{ width: "100%" }}>
        <Typography.Text>
          O assistente será treinado com todas as informações do texto.
        </Typography.Text>
        <Form layout={"vertical"} form={form} onFinish={onFinish}>
          <Form.Item
            label={"Titulo"}
            name={"title"}
            rules={[
              { required: true, message: "O titulo é obrigatório" },
              { min: 3, max: 48, message: "O titulo deve ter entre 3 e 48 caracteres" },
            ]}
          >
            <AntdInput placeholder={"Título das informações"} />
          </Form.Item>
          <Form.Item
            label={"Texto"}
            name={"text"}
            rules={[
              { required: true, message: "O texto é obrigatório" },
              { min: 10, message: "O texto deve ter mais de 10 caracteres" },
            ]}
          >
            <AntdInput.TextArea autoSize placeholder={"Adicione um texto..."} />
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <AntdButton htmlType={"submit"} />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
}

function DocumentEdit({ form, onFinish }) {
  return (
    <Form layout={"vertical"} onFinish={onFinish} form={form}>
      <Form.Item
        name={"title"}
        rules={[
          { required: true, min: 3, max: 48, message: "O título é obrigatório" },
        ]}
        label={"Título"}
      >
        <AntdInput placeholder={"Titulo do documento"} />
      </Form.Item>
      <Form.Item
        name={"link"}
        rules={[
          {
            pattern:
              // eslint-disable-next-line no-useless-escape
              /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/,
            message: "URL inválido",
          },
        ]}
        label={"Link"}
      >
        <AntdInput placeholder={"Link do documento"} />
      </Form.Item>
      <Form.Item style={{ display: "none" }}>
        <AntdButton htmlType={"submit"} />
      </Form.Item>
    </Form>
  );
}

function DocumentView({ assistantID, fileId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState("");

  const ContainerHeight = 500;

  const appendData = useCallback(() => {
    setLoading(true);
    getFileContent({ assistantID, fileId, offset })
      .then(({ data: newData, offset: newOffset }) => {
        setData((old) => [...old, ...newData]);
        setOffset(newOffset);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Falha ao consultar conteúdo dos arquivos");
      })
      .finally(() => setLoading(false));
  }, [assistantID, fileId, offset]);

  useEffect(() => {
    appendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = (e) => {
    if (
      Math.abs(e.currentTarget.scrollHeight - e.currentTarget.scrollTop - ContainerHeight) <= 1
    ) {
      if (offset) appendData();
    }
  };

  return (
    <>
      {data?.length <= 0 && (
        <div className="flex h-40 w-full items-center justify-center">
          <Spinner size={24} />
        </div>
      )}
      <VirtualList
        layout={"Vertical"}
        data={data}
        onScroll={onScroll}
        height={ContainerHeight}
        itemHeight={48}
        itemKey={"identifier"}
      >
        {(item) => (
          <div className="block py-2">
            <Markdown remarkPlugins={[remarkGfm]}>{item.content}</Markdown>
          </div>
        )}
      </VirtualList>
      {loading && data?.length > 0 && (
        <div className="flex h-20 w-full items-center justify-center">
          <Spinner size={20} />
        </div>
      )}
    </>
  );
}
